import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PowerOutageScheduleFilterSearchButton } from './PowerOutageScheduleFilterSearchButton';
import { PowerOutageScheduleFilterCheckbox } from './PowerOutageScheduleFilterCheckbox';
import { PowerOutageScheduleFilterSubmitButton } from './PowerOutageScheduleFilterSubmitButton';
import SVGImage from '../layout/SVGImage';
import { Divider } from '../common/Divider';

interface PowerOutageScheduleFilterProps {
  testId?: string;
  className?: string;
  onSubmit?: (
    type: PowerOutageScheduleFilterRadioType,
    filter: string,
    isLock: boolean
  ) => void;
  closeFilter?: () => void;
}

export enum PowerOutageScheduleFilterRadioType {
  Generator = 'generator',
  Availability = 'availability',
}

export const PowerOutageScheduleFilter: React.FC<
  PowerOutageScheduleFilterProps
> = ({ testId, className, onSubmit, closeFilter }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [isLock, setIsLock] = useState(false);
  const [selectedType, setSelectedType] = useState(
    PowerOutageScheduleFilterRadioType.Generator
  );

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value as PowerOutageScheduleFilterRadioType);
  };

  return (
    <div
      data-testId={testId}
      className={`flex flex-col bg-white p-[15px] pt-5 ${className}`}
    >
      <fieldset>
        <div className="mb-3 flex">
          <legend className="sr-only">filter</legend>
          <div className="flex items-center">
            <input
              id="by-generator"
              data-testid={`${testId}-radio-generator`}
              type="radio"
              name="filter"
              value="generator"
              className="peer hidden"
              onChange={handleRadioChange}
              defaultChecked
            />
            <SVGImage name="radio-on" className="hidden peer-checked:block" />
            <SVGImage name="radio-off" className="block peer-checked:hidden" />
            <label
              data-testid={`${testId}-label-generator`}
              htmlFor="by-generator"
              className="noto-11-medium m-1 block text-dark-3"
            >
              {t('overview.modal.filter.generator')}
            </label>
          </div>
          <div className="ml-[6px] flex items-center">
            <input
              id="by-availability"
              data-testid={`${testId}-radio-availability`}
              type="radio"
              name="filter"
              value="availability"
              onChange={handleRadioChange}
              className="peer hidden"
            />
            <SVGImage name="radio-on" className="hidden peer-checked:block" />
            <SVGImage name="radio-off" className="block peer-checked:hidden" />
            <label
              data-testid={`${testId}-label-availability`}
              htmlFor="by-availability"
              className="noto-11-medium m-1 block text-dark-3"
            >
              {t('overview.modal.filter.availability')}
            </label>
          </div>
        </div>
      </fieldset>
      <PowerOutageScheduleFilterSearchButton
        value={filter}
        onChange={setFilter}
        testId="add-power-outage-schedule-modal-filter-search-button"
      />
      <Divider className="mb-[10px] mt-[11px] bg-[#D6DEE4]" />
      <PowerOutageScheduleFilterCheckbox
        isChecked={isLock}
        onClick={() => setIsLock((prev) => !prev)}
        testId="add-power-outage-schedule-modal-filter-lock-checkbox"
        text={t('overview.modal.filter.lock')}
      />
      <PowerOutageScheduleFilterSubmitButton
        onSubmit={() => {
          onSubmit?.(selectedType, filter, isLock);
          closeFilter?.();
        }}
        className="mt-[15px] self-end"
        testId="add-power-outage-schedule-modal-filter-submit-button"
      />
    </div>
  );
};
